<template>
  <div
    class="activity__wrapper"
    :class="{
      ['no-activity-wrapper']: noActivity && _activityView === 'cards'
    }"
  >
    <div class="activity-heading-wrapper">
      <div>
        <h3>
          Activity
        </h3>
      </div>

      <div class="right-side-switch">
        <div class="switcher-wrapper">
          <VSwitcher
            :activeKey="dataKey"
            :switcherKeys="switcherKeys"
            :disabled="mailPiecesLoading"
            type="element"
            @change="_updateLayout"
            extraClass="contact-details-switcher"
          />
        </div>

        <div class="toggle-wrapper">
          <VToggleButton
            v-if="!noActivity"
            :items="toggleButtonItems"
            :activeId="_activityView"
            @click="_updateActivityView"
          />
        </div>
      </div>
    </div>

    <div
      v-if="_activityView === 'cards'"
      class="activity-cards__wrapper"
      :class="{
        ['no-activity']: noActivity
      }"
    >
      <WithLoader :isLoading="mailPiecesLoading">
        <div
          class="activity-wrapper"
          v-for="activity in contactActivities"
          :key="activity.id"
        >
          <ContactsActivityItem
            :id="activity.id"
            :imgLink="
              dataKey === 'letters'
                ? activity.thumbnail_url
                : activity.front_thumbnail_url
            "
            :templateName="activity.template && activity.template.name"
            :templateId="activity.template && activity.template.id"
            :templateType="activity.template && getSize(activity.template.type)"
            :templateDeleted="activity.template && activity.template.deleted_at"
            :source="activity.source"
            :created="activity.created_at"
            :currentStatus="activity.latestStatus"
            :doubleSided="!!activity.double_sided"
            :color="!!activity.color"
            :campaignId="activity.campaign && activity.campaign.id"
            :campaign_name="activity.campaign && activity.campaign.name"
            :campaignDeleted="activity.campaign && activity.campaign.deleted_at"
            :expanded="expandedActivity === activity.id"
            :activityTrackingItems="getActivityTrackingItems(activity)"
            :pdfLoading="downloadingPdfId === activity.id"
            :fileUrl="activity.url_to_file || activity.pdf_url"
            :type="dataKey"
            @expand="onExpand"
            @previewPdf="e => _downloadPdf(e, activity)"
          />
        </div>

        <div v-if="noActivity" class="no-activity__title">
          No activity found for this contact
        </div>
      </WithLoader>
    </div>
    <div v-else class="activity-table__wrapper">
      <WithLoader :isLoading="mailPiecesLoading">
        <ContactsDetailsActivityTable
          :activities="contactActivities"
          :expandedId="expandedActivity"
          :type="dataKey"
          @expand="onExpand"
        />
      </WithLoader>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue";
import { mapGetters, mapActions } from "vuex";

// components
import ContactsActivityItem from "@/components/ContactsActivityItem";
import VToggleButton from "@/components/VToggleButton";
import HistoryTable from "@/components/HistoryTable";
import ContactsDetailsActivityTable from "@/components/ContactsDetailsActivityTable";
import VSwitcher from "@/components/VSwitcher";
import WithLoader from "@/components/WithLoader";

import { getPostcardFullType, downloadURI } from "@/utils/util";
import { ActivityTrackingOrder } from "@/constants/common";
import { fetchMailPieceThumbnail } from "@/api-client/contacts";

//images
import postcardIcon from "@/assets/images/postcard-icon-light.svg";
import postcardIconActive from "@/assets/images/postcard-icon-active.svg";
import letterIcon from "@/assets/images/letter-icon.svg";
import letterIconActive from "@/assets/images/letter-icon-active.svg";

export default Vue.component("ContactsDetailsActivitySection", {
  props: {
    contact: VueTypes.object
  },

  components: {
    ContactsActivityItem,
    VToggleButton,
    HistoryTable,
    ContactsDetailsActivityTable,
    VSwitcher,
    WithLoader
  },

  data() {
    const defaultDataKey =
      localStorage.getItem("mailpieceOption") || "postcards";

    return {
      dataKey: defaultDataKey,
      downloadingPdfId: null,
      expandedActivity: null,
      toggleButtonItems: [
        {
          id: "table",
          icon: "list-ul"
        },
        {
          id: "cards",
          icon: "grip-lines"
        }
      ]
    };
  },

  methods: {
    _updateLayout(active) {
      this.dataKey = active;
      localStorage.setItem("mailpieceOption", this.dataKey);

      if (this.dataKey === "letters") {
        this.fetchLettersPiecesContact({ id: this.contact.id });
      } else {
        this.fetchMailPieces({ id: this.contact.id });
      }
    },

    _updateActivityView(key) {
      this.changeActivityView(key);
    },

    async _downloadPdf(e, activity) {
      e.stopPropagation();

      if (this.dataKey === "postcards") {
        e.stopPropagation();

        if (!activity.pdf_url) {
          return;
        }

        downloadURI(activity.pdf_url);
      } else {
        e.stopPropagation();

        if (!activity.url_to_file && !activity.pdf_url) {
          return;
        }

        const url_to_open = activity.url_to_file || activity.pdf_url;

        downloadURI(url_to_open);
      }
    },

    showFile(blob) {
      // It is necessary to create a new blob object with mime-type explicitly set
      // otherwise only Chrome works like it should
      const newBlob = new Blob([blob], { type: "application/pdf" });

      // IE doesn't allow using a blob object directly as link href
      // instead it is necessary to use msSaveOrOpenBlob
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(newBlob);
        return;
      }

      const data = window.URL.createObjectURL(newBlob);
      const newWindow = window.open("", "_blank");
      newWindow.location = data;
    },

    onExpand(id) {
      if (this.expandedActivity === id) {
        this.expandedActivity = null;
      } else {
        this.expandedActivity = id;
      }
    },

    getSize(type) {
      return getPostcardFullType(type);
    },

    getActivityTrackingItems(activity) {
      const obj = {
        latestStatus: activity.latestStatus
      };

      for (let key of ActivityTrackingOrder) {
        obj[key] = activity[key];
      }

      return obj;
    },

    ...mapActions("contacts", [
      "changeActivityView",
      "fetchLettersPiecesContact",
      "fetchMailPieces"
    ])
  },

  computed: {
    _dataSource() {
      return this.dataKey === "postcards"
        ? this.contact.activities
        : this.contact.letters;
    },

    switcherKeys() {
      return {
        left: {
          key: "postcards",
          label: "Postcards",
          icon: postcardIcon,
          iconActive: postcardIconActive
        },

        right: {
          key: "letters",
          label: "Letters",
          icon: letterIcon,
          iconActive: letterIconActive
        }
      };
    },

    _activityView() {
      return this.activityView;
    },

    noActivity() {
      return this.contactActivities && !this.contactActivities.length;
    },

    ...mapGetters("contacts", [
      "activityView",
      "contactActivities",
      "mailPiecesLoading"
    ])
  },

  created() {
    this._updateLayout(this.dataKey);
  }
});
</script>

<style lang="scss" scoped>
.activity__wrapper {
  &.no-activity-wrapper {
    @media screen and (min-width: $medium-screen) {
      width: calc(56% + 9px);
      max-width: calc(56% + 9px);
    }
  }
}

.activity-heading-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;

  h3 {
    color: $secondary-text-color;
    margin-bottom: 7px;
    padding-left: 10px;
    font-size: 22px;
    font-family: "Lato-Regular", Arial, sans-serif;
  }
}

.activity-cards__wrapper {
  //overflow-y: auto;
  //overflow-x: hidden;

  overflow: visible;

  &.no-activity {
    overflow: visible;

    .no-activity__title {
      padding-left: 8px;
      font-size: 18px;
    }
  }

  .activity-wrapper {
    margin-bottom: 20px;
  }
}

/deep/ .contact-details-switcher {
  height: 27px;
  padding-bottom: 2px;
}

.right-side-switch {
  display: flex;

  .switcher-wrapper .contact-details-switcher {
    display: flex;
    align-items: center;

    /deep/ .element-wrapper {
      font-size: 14px;
      height: 27px;
      padding: 0 5px;

      img {
        height: 15px;
      }
    }
  }

  .toggle-wrapper {
    margin-left: 15px;
  }
}
</style>

<style lang="scss">
.no-activity__title {
  font-size: 18px;
  padding-left: 10px;
}
</style>
